import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { 
  FaSync, FaPlus, FaPlay, FaPencilAlt, FaTrash, 
  FaAngleDown, FaAngleUp, FaCheck, FaTimes, FaClock,
  FaServer, FaDatabase, FaLaptop, FaBuilding, FaCalendarAlt,
  FaHistory, FaExclamationTriangle, FaInfoCircle, FaSearch
} from 'react-icons/fa';
import { BsLightningChargeFill } from 'react-icons/bs';

const TallyManagement = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const context = useContext(AuthContext);
  const companyHead = context.user.user;
  const createdBy = companyHead._id;
  
  const [syncs, setSyncs] = useState([]);
  const [editSync, setEditSync] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [scheduleType, setScheduleType] = useState(editSync?.type || "Duration");
  const [syncTypeData, setSyncTypeData] = useState(editSync?.syncTypeData || 'Incremental');
  const [expandedSync, setExpandedSync] = useState({});
  const [company, setCompany] = useState(null);
  const [tallyStatus, setTallyStatus] = useState(false);
  const [currentTallyCompany, setCurrentTallyCompany] = useState("");
  const [tallyCompanies, setTallyCompanies] = useState([]);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState({});
  
  // Polling references
  const pollingInterval = useRef(null);
  const activeSyncs = useRef({});
  const POLL_FREQUENCY = 5000; // Poll every 5 seconds
  
  // Add these state variables with your other state declarations
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedSyncLogs, setSelectedSyncLogs] = useState([]);
  const [selectedSyncName, setSelectedSyncName] = useState("");
  const [logFilters, setLogFilters] = useState({
    type: "all",
    search: "",
    dateRange: {
      start: null,
      end: null
    }
  });

  // Add these state variables for pagination
  const [logPagination, setLogPagination] = useState({
    currentPage: 1,
    logsPerPage: 10,
    totalPages: 1
  });
  const [confirmDeleteLogs, setConfirmDeleteLogs] = useState(false);

  // Add loading state for logs
  const [logsLoading, setLogsLoading] = useState(false);
  const [logCounts, setLogCounts] = useState({
    total: 0,
    success: 0,
    info: 0,
    warning: 0,
    error: 0
  });

  // Add this with your other state variables at the top
  const [currentSyncType, setCurrentSyncType] = useState(editSync?.TallySyncType || '');

  // Helper function to format current time as HH:mm
  const getCurrentTime = () => {
    const now = new Date();
    const hh = now.getHours().toString().padStart(2, "0");
    const mm = now.getMinutes().toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };

  // Add this near your other utility functions
  const clearSyncLoading = (syncId) => {
    setSyncLoading(prev => ({ ...prev, [syncId]: false }));
  };

  // Setup polling mechanism
  useEffect(() => {
    // Start polling for sync updates
    startPolling();
    
    // Clean up polling on unmount
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  // Start the polling mechanism
  const startPolling = () => {
    // Clear any existing interval first
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }
    
    // Set up new polling interval
    pollingInterval.current = setInterval(pollSyncUpdates, POLL_FREQUENCY);
  };

  // Function to poll for sync updates
  const pollSyncUpdates = async () => {
    try {
      // Always fetch all syncs to get the latest data
      const response = await axios.get(`${API_BASE}/tally-syncs/${createdBy}`);
      const fetchedSyncs = response.data;
      
      // Compare with current syncs to detect changes and update loading states
      if (fetchedSyncs && fetchedSyncs.length > 0) {
        // Check each sync for completion by comparing logs
        const updatedLoadingStates = {};
        
        for (const fetchedSync of fetchedSyncs) {
          // If we have a loading state for this sync
          if (syncLoading[fetchedSync._id] === true) {
            // Check if sync has completed by looking at the most recent log message
            const lastLogIndex = fetchedSync.logs?.length - 1;
            
            if (lastLogIndex >= 0) {
              const lastMessage = fetchedSync.logs[lastLogIndex].message;
              const completionKeywords = ['completed', 'finished', 'done', 'error', 'failed', 'Tally sync for vouchers completed'];
              
              // Check if any completion keywords are in the message
              const hasCompleted = completionKeywords.some(keyword => 
                lastMessage.toLowerCase().includes(keyword.toLowerCase())
              );
              
              // If the sync has completed or there's an error, turn off loading state
              if (hasCompleted) {
                updatedLoadingStates[fetchedSync._id] = false;
                
                // Show a completion notification
                if (activeSyncs.current[fetchedSync._id]) {
                  const isError = lastMessage.toLowerCase().includes('error') || lastMessage.toLowerCase().includes('failed');
                    Swal.fire('Sync Complete', 'The sync process has started successfully.', 'success');
                  // Remove from active syncs
                  delete activeSyncs.current[fetchedSync._id];
                }
              }
            }
          }
        }
        
        // Update loading states if any changes were detected
        if (Object.keys(updatedLoadingStates).length > 0) {
          setSyncLoading(prev => ({...prev, ...updatedLoadingStates}));
        }
      }
      
      // Update syncs regardless to keep data fresh
      setSyncs(fetchedSyncs);
      
    } catch (error) {
      console.error("Polling error:", error);
    }
  };

  // Initial fetch of company and sync data
  const fetchData = async () => {
    try {
      const companyRes = await axios.get(`${API_BASE}/companies/${companyHead.company._id || companyHead.company}`);
      setCompany(companyRes.data);
      const tallyUrl = companyRes.data.tallyUrl;
      const activeRes = await axios.post(`${API_BASE}/tally/is-tally-active`, { tallyUrl });
      setTallyStatus(activeRes.data.active);
      const currentRes = await axios.post(`${API_BASE}/tally/get-current-active-company`, { tallyUrl });
      setCurrentTallyCompany(currentRes.data.response);
      const companiesRes = await axios.post(`${API_BASE}/tally/get-tally-companies`, { tallyUrl });
      setTallyCompanies(companiesRes.data.response.map(c => c.NAME));
      const licenseRes = await axios.post(`${API_BASE}/tally/get-lincence`, { tallyUrl });
      setLicenseInfo(licenseRes.data.response);
      const syncsRes = await axios.get(`${API_BASE}/tally-syncs/${createdBy}`);
      setSyncs(syncsRes.data);
      
      // Initialize sync loading status based on fetched data
      const initialSyncLoading = {};
      syncsRes.data.forEach(sync => {
        initialSyncLoading[sync._id] = sync.status === 'running';
      });
      setSyncLoading(initialSyncLoading);
      
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch Tally data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [API_BASE, companyHead.company, createdBy]);

  // Refresh button handler to re-fetch syncs and logs
  const handleRefresh = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tally-syncs/${createdBy}`);
      setSyncs(res.data);
      
      // Update sync loading status
      const updatedSyncLoading = {};
      res.data.forEach(sync => {
        updatedSyncLoading[sync._id] = sync.status === 'running';
      });
      setSyncLoading(prev => ({...prev, ...updatedSyncLoading}));
      
      toast.success('Syncs refreshed successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to refresh syncs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    try {
      if (editSync) {
        await axios.put(`${API_BASE}/tally-syncs/${editSync._id}`, data);
        toast.success('Sync updated successfully');
      } else {
        await axios.post(`${API_BASE}/tally-syncs`, { ...data, createdBy });
        toast.success('Sync created successfully');
      }
      // Refresh to get updated syncs
      await handleRefresh();
      setOpenDialog(false);
      setEditSync(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    }
  };

  // Update your handleRunSync function to include error handling and SweetAlert confirmation
  const handleRunSync = async (id) => {
    // Use SweetAlert for confirmation
    const result = await Swal.fire({
      title: 'Run Tally Sync?',
      text: "This will synchronize your data with Tally",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, run sync!'
    });
    
    if (result.isConfirmed) {
      try {
        setSyncLoading(prev => ({ ...prev, [id]: true }));
        await axios.post(`${API_BASE}/tally-syncs/run/${id}`);
        toast.success('Sync started successfully');
        
        // Immediately poll once to get updated status
        setTimeout(pollSyncUpdates, 1000);
        
        // Increase polling frequency temporarily when a sync is running
        clearInterval(pollingInterval.current);
        pollingInterval.current = setInterval(pollSyncUpdates, 2000);
        
        // Register this sync as active so we can properly track its completion
        activeSyncs.current[id] = true;
        
        // Setup a safety timeout to clear loading state if other mechanisms fail
        setTimeout(() => {
          clearSyncLoading(id);
          
          // Reset to normal polling frequency
          clearInterval(pollingInterval.current);
          pollingInterval.current = setInterval(pollSyncUpdates, POLL_FREQUENCY);
        }, 60000);
        
      } catch (err) {
        clearSyncLoading(id);
      }
    }
  };

  const handleDeleteSync = async (id) => {
    // Use SweetAlert for confirmation
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this deletion!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });
    
    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_BASE}/tally-syncs/${id}`);
        setSyncs(syncs.filter(s => s._id !== id));
        Swal.fire(
          'Deleted!',
          'Your sync configuration has been deleted.',
          'success'
        );
      } catch (err) {
        Swal.fire(
          'Error!',
          err.response?.data?.message || 'Failed to delete sync',
          'error'
        );
      }
    }
  };

  const toggleAccordion = (id) => {
    setExpandedSync(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const viewLogsInModal = async (sync) => {
    setSelectedSyncName(sync.name);
    setShowLogModal(true);
    
    // Reset filters and pagination
    setLogFilters({
      type: "all",
      search: "",
      dateRange: {
        start: null,
        end: null
      }
    });
    
    setLogPagination({
      currentPage: 1,
      logsPerPage: 10,
      totalPages: Math.ceil((sync.logs || []).length / 10)
    });
    
    // Fetch initial logs
    await fetchFilteredLogs(sync._id);
  };

  const fetchFilteredLogs = async (syncId) => {
    if (!syncId) {
      // Find the sync ID based on the selected sync name
      const sync = syncs.find(s => s.name === selectedSyncName);
      if (sync) {
        syncId = sync._id;
      } else {
        return;
      }
    }
    
    try {
      setLogsLoading(true);
      
      // Prepare query parameters
      const params = new URLSearchParams({
        page: logPagination.currentPage,
        limit: logPagination.logsPerPage,
        type: logFilters.type,
        search: logFilters.search
      });
      
      if (logFilters.dateRange.start) {
        params.append('startDate', logFilters.dateRange.start);
      }
      
      if (logFilters.dateRange.end) {
        params.append('endDate', logFilters.dateRange.end);
      }
      
      const response = await axios.get(`${API_BASE}/tally-syncs/${syncId}/logs?${params.toString()}`);
      
      // Update state with response data
      setSelectedSyncLogs(response.data.logs);
      setLogCounts(response.data.logCounts);
      setLogPagination(prev => ({
        ...prev,
        currentPage: response.data.pagination.page,
        totalPages: response.data.pagination.totalPages
      }));
      
    } catch (error) {
      toast.error('Failed to fetch logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setLogPagination(prev => ({
      ...prev,
      currentPage: pageNumber
    }));
  };

  useEffect(() => {
    if (showLogModal) {
      fetchFilteredLogs();
    }
  }, [logPagination.currentPage, logPagination.logsPerPage, logFilters, showLogModal]);

  const handleFilterChange = (newFilters) => {
    setLogFilters(newFilters);
    // Reset to first page when filters change
    setLogPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    // Fetch will be triggered by the useEffect
  };

  const handleExportLogs = async () => {
    try {
      const syncId = syncs.find(s => s.name === selectedSyncName)?._id;
      if (!syncId) return;
      
      setLogsLoading(true);
      
      // Get all logs with current filters but no pagination
      const params = new URLSearchParams({
        limit: 10000, // Large limit to get all logs
        type: logFilters.type,
        search: logFilters.search
      });
      
      if (logFilters.dateRange.start) {
        params.append('startDate', logFilters.dateRange.start);
      }
      
      if (logFilters.dateRange.end) {
        params.append('endDate', logFilters.dateRange.end);
      }
      
      const response = await axios.get(`${API_BASE}/tally-syncs/${syncId}/logs?${params.toString()}`);
      
      // Create CSV content from all logs
      const csvContent = 
        "data:text/csv;charset=utf-8," + 
        "Timestamp,Message,Severity\n" +
        response.data.logs.map(log => {
          // Get severity for each log
          const message = log.message.toLowerCase();
          let severity = "info";
          
          if ((message.includes('error') || message.includes('failed') || message.includes('timeout')) && 
              !message.includes('errored: 0')) {
            severity = "error";
          } else if (message.includes('warning') || message.includes('attention') || 
                    message.includes('could not') || message.includes('retry')) {
            severity = "warning";
          } else if (message.includes('completed') || message.includes('updated:') || 
                    message.includes('created:') || message.includes('sync started') ||
                    message.includes('tally is active') || message.includes('verified') ||
                    message.includes('parsed') || (message.includes('errored: 0') && 
                    (message.includes('updated:') || message.includes('created:')))) {
            severity = "success";
          }
          
          return `"${new Date(log.timestamp).toLocaleString()}","${log.message.replace(/"/g, '""')}","${severity}"`;
        }).join("\n");
      
      // Create download link
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${selectedSyncName.replace(/\s+/g, '-')}_logs.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      toast.error('Failed to export logs');
      console.error('Error exporting logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDeleteAllLogs = async (syncId) => {
    try {
      const syncToDelete = syncs.find(s => s._id === syncId);
      if (!syncToDelete) {
        toast.error('Sync record not found');
        setConfirmDeleteLogs(false);
        return;
      }
      
      const syncName = syncToDelete.name;
      
      // Show loading state
      toast.info(`Deleting logs for ${syncName}...`);
      
      const response = await axios.delete(`${API_BASE}/tally-syncs/${syncId}/logs`);
      
      if (response.data.success) {
        // Update local state to reflect deleted logs
        setSyncs(prev => prev.map(sync => 
          sync._id === syncId 
            ? {...sync, logs: []} 
            : sync
        ));
        
        // Close the log modal and confirmation dialog
        setShowLogModal(false);
        setConfirmDeleteLogs(false);
        
        // Reset log counts
        setLogCounts({
          total: 0,
          success: 0,
          info: 0,
          warning: 0,
          error: 0
        });
        
        toast.success(`Logs for "${syncName}" deleted successfully`);
      } else {
        toast.error(response.data.message || 'Failed to delete logs');
      }
    } catch (err) {
      console.error('Error deleting logs:', err);
      toast.error(err.response?.data?.message || 'Failed to delete logs');
      setConfirmDeleteLogs(false);
    }
  };

  const getLogSeverity = (message) => {
    message = message.toLowerCase();
    
    // Error cases (but not "errored: 0")
    if ((message.includes('error') || message.includes('failed') || message.includes('timeout') || 
        message.includes('errored') || message.includes('errors') || message.includes('skipped')) && 
        !message.includes('errored: 0') && !message.includes('skipped: 0') && !message.includes('Errors 0') &&
        !message.includes('skipped 0')) {
      return 'error';
    }
    
    // Warning cases
    if (message.includes('warning') || message.includes('attention') || 
        message.includes('could not') || message.includes('retry')) {
      return 'warning';
    }
    
    // Success cases
    if (message.includes('completed') || 
        message.includes('Completed') ||
        message.includes('updated:') || 
        message.includes('Updated:') ||
        message.includes('created:') ||
        message.includes('Created:') ||
        message.includes('sync completed') ||
        message.includes('sync started') || 
        message.includes('tally is active') || 
        message.includes('verified') || 
        message.includes('parsed') || 
        (message.includes('errored: 0') && (message.includes('updated:') || message.includes('created:')))) {
      return 'success';
    }
    
    // Info cases (default for any other messages)
    return 'info';
  };

  useEffect(() => {
    if (editSync) {
      setScheduleType(editSync.type || "Duration");
      setCurrentSyncType(editSync.TallySyncType || '');
      
      // Ensure syncTypeData is always 'Full' for certain sync types
      if (["Godowns", "Stock Groups", "Reorder Status", "Stock Summary", "Groups", "Units", "Ledger", "Voucher Types"].includes(editSync.TallySyncType)) {
        setSyncTypeData('Full');
      } else {
        setSyncTypeData(editSync.syncTypeData || 'Incremental');
      }
    } else {
      setScheduleType("Duration");
      setCurrentSyncType('');
      setSyncTypeData('Incremental');
    }
  }, [editSync]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      <div className="w-full mx-auto">
        {/* Header with logo */}
        <div className="mb-6 flex items-center justify-between bg-white p-5 rounded-lg shadow-md">
          <div className="flex items-center">
            <FaDatabase className="text-4xl text-indigo-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
                Tally Management
              </h1>
              <p className="text-gray-600">{company?.name || "Company"}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className={`flex items-center px-3 py-1 rounded-full text-sm font-medium 
              ${tallyStatus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              <span className={`w-2 h-2 mr-2 rounded-full ${tallyStatus ? 'bg-green-500' : 'bg-red-500'}`}></span>
              {tallyStatus ? 'Tally Connected' : 'Tally Disconnected'}
            </span>
          </div>
        </div>

        {/* Info Panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Tally Status Card */}
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-5 flex items-start">
            <div className="bg-indigo-100 p-3 rounded-lg mr-4">
              <FaServer className="text-indigo-600 text-xl" />
            </div>
            <div>
              <h3 className="text-gray-500 font-medium text-sm uppercase tracking-wider mb-1">Tally Status</h3>
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-2 ${tallyStatus ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className="font-semibold text-gray-800">{tallyStatus ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
          </div>

          {/* Current Company Card */}
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-5 flex items-start">
            <div className="bg-blue-100 p-3 rounded-lg mr-4">
              <FaBuilding className="text-blue-600 text-xl" />
            </div>
            <div>
              <h3 className="text-gray-500 font-medium text-sm uppercase tracking-wider mb-1">Current Company</h3>
              <p className="font-semibold text-gray-800 truncate max-w-[200px]">{currentTallyCompany || 'N/A'}</p>
            </div>
          </div>

          {/* License Info Card */}
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-5 flex items-start">
            <div className="bg-yellow-100 p-3 rounded-lg mr-4">
              <FaLaptop className="text-yellow-600 text-xl" />
            </div>
            <div>
              <h3 className="text-gray-500 font-medium text-sm uppercase tracking-wider mb-1">License Info</h3>
              {licenseInfo ? (
                <div className="space-y-1 text-sm font-medium text-gray-800">
                  <p className="truncate">S/N: {licenseInfo.SERIALNUMBER?.substring(0, 8) || 'N/A'}</p>
                  <p className="text-xs text-gray-600">User: {licenseInfo.USERLEVEL || 'N/A'}</p>
                </div>
              ) : (
                <p className="text-gray-800">Not Available</p>
              )}
            </div>
          </div>

          {/* Companies Card */}
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-5 flex items-start">
            <div className="bg-green-100 p-3 rounded-lg mr-4">
              <FaDatabase className="text-green-600 text-xl" />
            </div>
            <div className="w-full">
              <h3 className="text-gray-500 font-medium text-sm uppercase tracking-wider mb-1">Tally Companies</h3>
              <p className="font-semibold text-gray-800">{tallyCompanies.length} companies available</p>
              <details className="mt-1">
                <summary className="text-xs text-indigo-600 cursor-pointer">Show all</summary>
                <ul className="mt-2 max-h-32 overflow-y-auto text-sm text-gray-600">
                  {tallyCompanies.map((comp, index) => (
                    <li key={index} className="truncate py-1 border-b border-gray-100">{comp}</li>
                  ))}
                </ul>
              </details>
            </div>
          </div>
        </div>

        {/* Sync Configurations Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 bg-white p-5 rounded-lg shadow-sm">
          <h3 className="flex items-center text-xl font-bold text-gray-800">
            <FaCalendarAlt className="mr-2 text-indigo-600" />
            Sync Configurations
          </h3>
          <div className="flex space-x-3 mt-3 md:mt-0">
            <button 
              onClick={handleRefresh} 
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 
                        flex items-center transition-colors duration-200 shadow-sm"
            >
              <FaSync className="mr-2" /> Refresh
            </button>
            <button 
              onClick={() => setOpenDialog(true)} 
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 
                        flex items-center transition-colors duration-200 shadow-sm"
              disabled={!tallyStatus}
            >
              <FaPlus className="mr-2" /> Create Sync
            </button>
          </div>
        </div>

        {/* Auto-update Notice */}
        <div className="mb-6 p-3 bg-blue-50 border-l-4 border-blue-500 rounded text-blue-800 flex items-center">
          <FaInfoCircle className="text-blue-500 mr-2" />
          <span className="text-sm">
            Logs auto-update every {POLL_FREQUENCY/1000} seconds. Last updated: {new Date().toLocaleTimeString()}
          </span>
        </div>

        {/* Syncs Table */}
        <div className="overflow-hidden bg-white rounded-xl shadow-md mb-8">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {syncs.length > 0 ? (
                syncs.map(sync => (
                  <React.Fragment key={sync._id}>
                    <tr className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-gray-800 font-medium">
                        {sync.name} <span className="text-gray-500 text-xs">({sync.id})</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {sync.TallySyncType}
                        </span>
                        {sync.syncTypeData && (
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                            ${sync.syncTypeData === 'Incremental' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                            {sync.syncTypeData}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaClock className="text-gray-500 mr-2" />
                          <span>
                            {sync.type}: 
                            <span className="font-medium ml-1">
                              {sync.type === 'Duration' ? `${sync.duration} mins` : sync.time}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <button 
                            onClick={() => handleRunSync(sync._id)} 
                            className="text-green-600 hover:text-green-800 flex items-center transition-colors duration-200"
                            disabled={syncLoading[sync._id]}
                          >
                            {syncLoading[sync._id] ? (
                              <div className="animate-spin h-4 w-4 mr-1 rounded-full border-2 border-green-600 border-t-transparent"></div>
                            ) : (
                              <FaPlay className="mr-1" />
                            )}
                            Run
                          </button>
                          <button 
                            onClick={() => { setEditSync(sync); setOpenDialog(true); setScheduleType(sync.type); }} 
                            className="text-blue-600 hover:text-blue-800 transition-colors duration-200 flex items-center"
                          >
                            <FaPencilAlt className="mr-1" /> Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteSync(sync._id)} 
                            className="text-red-600 hover:text-red-800 transition-colors duration-200 flex items-center"
                          >
                            <FaTrash className="mr-1" /> Delete
                          </button>
                          <button 
                            onClick={() => toggleAccordion(sync._id)}
                            className={`${expandedSync[sync._id] ? 'bg-gray-200' : ''} 
                              px-3 py-1 rounded text-gray-700 hover:bg-gray-200 transition-colors duration-200 flex items-center`}
                          >
                            {expandedSync[sync._id] ? <FaAngleUp className="mr-1" /> : <FaAngleDown className="mr-1" />}
                            Logs
                          </button>
                          <button 
                            onClick={() => viewLogsInModal(sync)}
                            className="bg-indigo-100 px-3 py-1 rounded text-indigo-700 hover:bg-indigo-200 transition-colors duration-200 flex items-center"
                          >
                            <FaHistory className="mr-1" /> View All
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedSync[sync._id] && (
                      <tr>
                        <td colSpan="4" className="bg-gray-50 p-0">
                          <div className="max-h-64 overflow-y-auto">
                            {sync.logs && sync.logs.length > 0 ? (
                              <div className="p-4">
                                <div className="flex justify-between items-center mb-3">
                                  <h4 className="font-semibold text-gray-700 flex items-center">
                                    <FaHistory className="mr-2 text-indigo-500" /> Recent Logs
                                  </h4>
                                  <div className="flex items-center">
                                    <span className="text-xs text-gray-500 mr-3">{sync.logs.length} entries</span>
                                    <button 
                                      onClick={() => pollSyncUpdates()} 
                                      className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center"
                                    >
                                      <FaSync className="mr-1 text-xs" /> Refresh Logs
                                    </button>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  {sync.logs.slice().reverse().slice(0, 20).map((log, index) => {
                                    const severity = getLogSeverity(log.message.toLowerCase());
                                    return (
                                      <div 
                                        key={index} 
                                        className={`p-3 rounded-lg border-l-4 flex items-start animate-fadeIn
                                          ${severity === 'error' ? 'bg-red-50 border-red-500' : 
                                           severity === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                                           severity === 'success' ? 'bg-green-50 border-green-500' : 
                                           'bg-blue-50 border-blue-500'}`}
                                      >
                                        <div className="mr-3 mt-0.5">
                                          {severity === 'error' ? <FaExclamationTriangle className="text-red-500" /> : 
                                           severity === 'warning' ? <FaExclamationTriangle className="text-yellow-500" /> :
                                           severity === 'success' ? <FaCheck className="text-green-500" /> :
                                           <FaInfoCircle className="text-blue-500" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm text-gray-800">
                                            {/* Highlight key metrics in sync result messages */}
                                            {log.message.includes('Updated:') || log.message.includes('Created:') || log.message.includes('Errored:') ? (
                                              <span dangerouslySetInnerHTML={{ 
                                                __html: log.message
                                                  .replace(/Updated:\s*(\d+)/gi, 'Updated: <span class="font-semibold text-green-600">$1</span>')
                                                  .replace(/Created:\s*(\d+)/gi, 'Created: <span class="font-semibold text-green-600">$1</span>')
                                                  .replace(/Errored:\s*0/gi, 'Errored: <span class="font-semibold text-green-600">0</span>')
                                                  .replace(/Errored:\s*([1-9]\d*)/gi, 'Errored: <span class="font-semibold text-red-600">$1</span>')
                                                  .replace(/Sync started/gi, '<span class="font-semibold text-blue-600">Sync started</span>')
                                                  .replace(/Tally is active/gi, '<span class="font-semibold text-green-600">Tally is active</span>')
                                                  .replace(/verified/gi, '<span class="font-semibold text-green-600">verified</span>')
                                                  .replace(/Parsed/gi, '<span class="font-semibold text-green-600">Parsed</span>')
                                              }} />
                                            ) : (
                                              <span dangerouslySetInnerHTML={{ 
                                                __html: log.message
                                                  .replace(/Sync started/gi, '<span class="font-semibold text-blue-600">Sync started</span>')
                                                  .replace(/Tally is active/gi, '<span class="font-semibold text-green-600">Tally is active</span>')
                                                  .replace(/verified/gi, '<span class="font-semibold text-green-600">verified</span>')
                                                  .replace(/Parsed/gi, '<span class="font-semibold text-green-600">Parsed</span>')
                                              }} />
                                            )}
                                          </p>
                                          <p className="text-xs text-gray-500 mt-1">
                                            {new Date(log.timestamp).toLocaleString()}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div> 
                            ) : (
                              <div className="p-8 text-center text-gray-500 italic">
                                No logs available. Run this sync to generate logs.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <FaDatabase className="text-gray-300 text-4xl mb-3" />
                      <p>No sync configurations found.</p>
                      <button 
                        onClick={() => setOpenDialog(true)} 
                        className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
                        disabled={!tallyStatus}
                      >
                        <FaPlus className="mr-2" /> Create Your First Sync
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sync Configuration Modal */}
        {openDialog && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity" onClick={() => { setOpenDialog(false); setEditSync(null); }}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-all">
              <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md transform transition-all scale-in-center" 
                   onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 flex items-center">
                    <BsLightningChargeFill className="text-yellow-500 mr-2" />
                    {editSync ? 'Edit' : 'Create'} Sync
                  </h3>
                  <button 
                    onClick={() => { setOpenDialog(false); setEditSync(null); }}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sync Name</label>
                    <input 
                      name="name" 
                      defaultValue={editSync?.name} 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" 
                      placeholder="Enter a descriptive name"
                      required 
                    />
                  </div>
                  <div>
                    <div className="flex items-center mb-1">
                      <label className="block text-sm font-medium text-gray-700">Sync Type</label>
                      <div className="ml-2 group relative">
                        <span className="cursor-help text-blue-500">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </span>
                        <div className="hidden group-hover:block absolute z-10 w-80 bg-white border border-gray-300 rounded-lg p-3 shadow-lg text-sm text-gray-700">
                          <p className="font-bold mb-1">Voucher Sync Options:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li><span className="font-semibold">Vouchers (Complete):</span> Performs both list and detail sync in one operation. Use for complete initial setup.</li>
                            <li><span className="font-semibold">Voucher List:</span> Only syncs basic voucher metadata (numbers, dates, types). Fast but doesn't include line items.</li>
                            <li><span className="font-semibold">Voucher Details:</span> Updates existing vouchers with detailed line items. Run after a Voucher List sync.</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <select 
                      name="TallySyncType" 
                      value={currentSyncType}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                      onChange={(e) => {
                        const selectedType = e.target.value;
                        setCurrentSyncType(selectedType);
                        
                        // Remove 'Voucher Details' from this list
                        if(['Godowns', 'Stock Groups', 'Reorder Status', 'Stock Summary', 'Groups', 'Units', 'Ledger', 'Voucher Types', 'Simple Trial Balance'].includes(selectedType)) {
                          setSyncTypeData('Full');
                        }
                      }}
                    >
                      <option value="Stock Items">Stock Items</option>
                      {company?.isStockStatus && (
                        <option value="Stock Summary">Stock Summary</option>
                      )}
                      <option value="Godowns">Godowns</option>
                      {company?.isStockStatus && (
                        <option value="Reorder Status">Reorder Status</option>
                      )}
                      <option value="Stock Groups">Stock Groups</option>
                      <option value="Groups">Groups</option>
                      <option value="Units">Units</option>
                      <option value="Ledger">Ledger</option>
                      <option value="Voucher Types">Voucher Types</option>
                      <option value="Simple Trial Balance">Simple Trial Balance</option>
                      <optgroup label="Voucher Sync Options">
                        <option value="Vouchers">Vouchers (Complete Sync)</option>
                        <option value="Voucher List">Voucher List (Metadata Only)</option>
                        <option value="Voucher Details">Voucher Details (Line Items)</option>
                      </optgroup>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sync Data Type</label>
                    <div className="grid grid-cols-2 gap-4 mt-1">
                      <label className={`flex items-center justify-center border rounded-lg px-3 py-2 cursor-pointer transition-colors 
                        ${syncTypeData === 'Incremental' ? 
                          'border-indigo-500 bg-indigo-50 text-indigo-800' : 
                          'border-gray-300 hover:bg-gray-50'} 
                        ${['Godowns', 'Stock Groups', 'Reorder Status', 'Stock Summary', 'Groups', 'Units', 'Ledger', 'Voucher Types', 'Simple Trial Balance'].includes(currentSyncType) ? 'opacity-50' : ''}`}>
                        <input 
                          type="radio" 
                          name="syncTypeData" 
                          value="Incremental" 
                          checked={syncTypeData === 'Incremental'}
                          onChange={() => setSyncTypeData('Incremental')}
                          className="sr-only" 
                          disabled={['Godowns', 'Stock Groups', 'Reorder Status', 'Stock Summary', 'Groups', 'Units', 'Ledger', 'Voucher Types', 'Simple Trial Balance'].includes(currentSyncType)}
                        />
                        <FaSync className="mr-2" />
                        Incremental
                      </label>
                      <label className={`flex items-center justify-center border rounded-lg px-3 py-2 cursor-pointer transition-colors 
                        ${syncTypeData === 'Full' ? 
                          'border-indigo-500 bg-indigo-50 text-indigo-800' : 
                          'border-gray-300 hover:bg-gray-50'}`}>
                        <input 
                          type="radio" 
                          name="syncTypeData" 
                          value="Full" 
                          checked={syncTypeData === 'Full'}
                          onChange={() => setSyncTypeData('Full')}
                          className="sr-only" 
                        />
                        <FaDatabase className="mr-2" />
                        Full
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Type</label>
                    <div className="grid grid-cols-2 gap-4 mt-1"> 
                      <label className={`flex items-center justify-center border rounded-lg px-3 py-2 cursor-pointer transition-colors 
                        ${scheduleType === 'Duration' ? 
                          'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-gray-300 hover:bg-gray-50'}`}>
                        <input 
                          type="radio" 
                          name="type" 
                          value="Duration" 
                          checked={scheduleType === 'Duration'}
                          onChange={(e) => setScheduleType(e.target.value)}
                          className="sr-only" 
                        />
                        <FaSync className="mr-2" />
                        Interval
                      </label>
                      <label className={`flex items-center justify-center border rounded-lg px-3 py-2 cursor-pointer transition-colors 
                        ${scheduleType === 'Time' ? 
                          'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-gray-300 hover:bg-gray-50'}`}>
                        <input 
                          type="radio" 
                          name="type" 
                          value="Time" 
                          checked={scheduleType === 'Time'}
                          onChange={(e) => setScheduleType(e.target.value)}
                          className="sr-only" 
                        />
                        <FaClock className="mr-2" />
                        Daily Time
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {scheduleType === 'Time' ? 'Specific Time' : 'Interval (minutes)'}
                    </label>
                    <input 
                      name={scheduleType === 'Time' ? 'time' : 'duration'} 
                      type={scheduleType === 'Time' ? 'time' : 'number'} 
                      min={scheduleType === 'Time' ? undefined : "1"}
                      defaultValue={
                        scheduleType === 'Time'
                          ? (editSync?.time ? editSync.time : getCurrentTime())
                          : (editSync?.duration || '60')
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" 
                      required 
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {scheduleType === 'Time' 
                        ? 'Sync will run once daily at this time' 
                        : 'Sync will run repeatedly at this interval'}
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button 
                      type="button" 
                      onClick={() => { setOpenDialog(false); setEditSync(null); }} 
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                    >
                      <FaCheck className="mr-2" /> 
                      {editSync ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}

        {/* Logs Modal */}
        {showLogModal && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity" onClick={() => setShowLogModal(false)}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-all">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl h-[80vh] flex flex-col transform transition-all scale-in-center"
                   onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-800 flex items-center">
                    <FaHistory className="text-indigo-500 mr-2" />
                    Logs for {selectedSyncName}
                  </h3>
                  <div className="flex items-center">
                    <button 
                      onClick={() => {
                        const syncId = syncs.find(sync => sync.name === selectedSyncName)?._id;
                        if (syncId) {
                          handleDeleteAllLogs(syncId);
                        } else {
                          toast.error('Sync record not found');
                        }
                      }}
                      className="text-red-600 hover:text-red-800 mr-4 flex items-center"
                      disabled={selectedSyncLogs.length === 0}
                    >
                      <FaTrash className="mr-1" /> Delete All Logs
                    </button>
                    <button 
                      onClick={() => setShowLogModal(false)} 
                      className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Filters Section */}
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4">
                    <div className="flex-1">
                      <label className="text-sm text-gray-600 mb-1 block">Search Messages</label>
                      <input 
                        type="text" 
                        value={logFilters.search}
                        onChange={(e) => handleFilterChange({...logFilters, search: e.target.value})}
                        placeholder="Filter by message content..." 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Status Type</label>
                      <select 
                        value={logFilters.type}
                        onChange={(e) => handleFilterChange({...logFilters, type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="all">All Messages</option>
                        <option value="success">Success</option>
                        <option value="info">Informational</option>
                        <option value="warning">Warnings</option>
                        <option value="error">Errors</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Date Range</label>
                      <div className="flex space-x-2 items-center">
                        <input 
                          type="date" 
                          value={logFilters.dateRange.start || ''}
                          onChange={(e) => handleFilterChange({
                            ...logFilters, 
                            dateRange: {...logFilters.dateRange, start: e.target.value}
                          })}
                          className="w-32 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <span className="text-gray-400">to</span>
                        <input 
                          type="date" 
                          value={logFilters.dateRange.end || ''}
                          onChange={(e) => handleFilterChange({
                            ...logFilters, 
                            dateRange: {...logFilters.dateRange, end: e.target.value}
                          })}
                          className="w-32 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <button 
                          onClick={() => handleFilterChange({
                            ...logFilters,
                            dateRange: { start: null, end: null }
                          })} 
                          className="text-gray-500 hover:text-gray-700"
                          title="Clear date filter"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Filter Status Bar */}
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">
                        Showing <span className="font-medium">{selectedSyncLogs.length}</span> of <span className="font-medium">{logCounts.total}</span> logs
                      </span>
                      {/* Log type indicator counts */}
                      <div className="flex ml-4 space-x-3">
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-green-500 mr-1"></div>
                          Success: {logCounts.success}
                        </span>
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mr-1"></div>
                          Info: {logCounts.info}
                        </span>
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-yellow-500 mr-1"></div>
                          Warning: {logCounts.warning}
                        </span>
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-red-500 mr-1"></div>
                          Error: {logCounts.error}
                        </span>
                      </div>
                    </div>
                    
                    {/* Display logs per page selector */}
                    <div className="flex items-center">
                      <span className="text-gray-600 mr-2">Logs per page:</span>
                      <select 
                        value={logPagination.logsPerPage}
                        onChange={(e) => setLogPagination({
                          ...logPagination,
                          logsPerPage: parseInt(e.target.value),
                          currentPage: 1
                        })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                    
                    <button 
                      onClick={() => handleFilterChange({
                        type: "all",
                        search: "",
                        dateRange: {
                          start: null,
                          end: null
                        }
                      })} 
                      className="text-indigo-600 hover:text-indigo-800"
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
                
                {/* Logs Table */}
                <div className="flex-1 overflow-y-auto">
                  {logsLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Timestamp
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Message
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Severity
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedSyncLogs.map((log, index) => {
                          const severity = getLogSeverity(log.message.toLowerCase());
                          return (
                            <tr key={index} className={`hover:bg-gray-50 transition-colors duration-150 
                              ${severity === 'error' ? 'bg-red-50' : 
                               severity === 'warning' ? 'bg-yellow-50' :
                               severity === 'success' ? 'bg-green-50' : 
                               'bg-blue-50'}`}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(log.timestamp).toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                                {log.message}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                                {severity.charAt(0).toUpperCase() + severity.slice(1)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                
                {/* Pagination */}
                <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <button 
                    onClick={() => handlePageChange(logPagination.currentPage - 1)} 
                    className="text-indigo-600 hover:text-indigo-800"
                    disabled={logPagination.currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {logPagination.currentPage} of {logPagination.totalPages}
                  </span>
                  <button 
                    onClick={() => handlePageChange(logPagination.currentPage + 1)} 
                    className="text-indigo-600 hover:text-indigo-800"
                    disabled={logPagination.currentPage === logPagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TallyManagement;